import {IClientRoute} from '@src/@interfaces/services/Router';
import AbstractRoute from './AbstractRoute';
import {RouteAction} from '@src/@types/services/Router/RouteAction';
import {RouteType} from '@src/@types/services/Router/RouteTypes';
import app from '@src/helpers/app';

export default class ClientRoute extends AbstractRoute<IClientRoute> implements IClientRoute {
    protected name: string | null = null;
    protected path!: string;
    protected action: RouteAction | null = null;
    protected redirect: string | null = null;
    protected permissions: string | string[] | null = null;
    protected type: RouteType = 'protected';

    getPath(): string {
        return this.path;
    }

    setPath(path: string): void {
        this.path = path;
    }

    getAction(): RouteAction | null {
        return this.action;
    }

    getType(): RouteType {
        return this.type;
    }

    setType(path: RouteType): void {
        this.type = path;
    }

    getRedirectPath(): string | null {
        return this.redirect;
    }

    isProtected(): boolean {
        return this.getType() === 'protected';
    }

    isPublic(): boolean {
        return this.getType() === 'public';
    }

    isRedirect(): boolean {
        return this.getRedirectPath() !== null;
    }

    setAction(action: RouteAction): void {
        this.action = action;
    }

    setRedirectPath(redirectTo: string): void {
        this.redirect = redirectTo;
    }

    getPermissions(): string | string[] | null {
        return this.permissions ?? this.guessPermissions();
    }

    setPermissions(permissions: string | string[] | null): void {
        this.permissions = permissions;
    }

    protected getRouteUri(): string {
        return this.getPath() || '';
    }

    protected parseName(name: string) {
        return app('controllers').parseRouteAction(name);
    }

    protected guessPermissions(): string | string[] | null {
        const name = this.getName();
        if (!name) {
            return null;
        }

        const {controllerName, controllerAction} = this.parseName(name);
        if (!controllerAction) {
            return null;
        }

        const resolver = () => app('names-resolver').permissions;

        const specificActions = {
            index: () => resolver().getIndexRoutePermissionName(controllerName),
            import: () => resolver().getImportRoutePermissionName(controllerName),
            show: () => resolver().getShowRoutePermissionName(controllerName),
            store: () => resolver().getCreateRoutePermissionName(controllerName),
            create: () => resolver().getCreateRoutePermissionName(controllerName),
            update: () => resolver().getUpdateRoutePermissionName(controllerName),
            delete: () => resolver().getDeleteRoutePermissionName(controllerName),
            destroy: () => resolver().getDeleteRoutePermissionName(controllerName),
            restore: () => resolver().getRestoreRoutePermissionName(controllerName),
        };

        const isSpecificAction = function (action: string): action is keyof typeof specificActions {
            return Object.keys(specificActions).includes(action);
        };

        if (isSpecificAction(controllerAction)) {
            return specificActions[controllerAction]();
        }

        return null;
    }
}
